import { pl } from 'date-fns/locale';
import { END_AFTER_START_MESSAGE } from '@ooh/contracts';
import { DatePickerButton } from '@ooh/ui/components/date-picker-button';
import { FormField } from '@ooh/ui/components/form-field';
import { Label } from '@ooh/ui/components/label';
import { SearchableSelectField } from '@ooh/ui/components/searchable-select-field';
import type { TFunction } from 'i18next';
import { Controller, useController, type UseFormReturn } from 'react-hook-form';
import { CampaignDurationField } from '@ooh/screens';
import { formatDateValue, parseOptionalDateInputValue } from '@ooh/ui/lib/date-input-helpers';
import { BRANDING_MODE_OPTIONS } from '../lib/offer-constants';
import type { CreateOfferFormValues } from './create-offer-page';

interface DraftDefaultsSectionProps {
  form: UseFormReturn<CreateOfferFormValues>;
  disabled: boolean;
  t: TFunction<'offers'>;
}

/**
 * `defaultDateTo`'s only custom validation issue is the cross-field
 * `withEndAfterStart` check in `CreateOfferSchema`, which carries the raw,
 * untranslated `END_AFTER_START_MESSAGE` constant — translate it before
 * showing it to the user (CU-86cb1nqwv).
 */
function getDefaultDateToErrorMessage(message: string | undefined, t: TFunction<'offers'>) {
  return message === END_AFTER_START_MESSAGE ? t('create.errors.dateRangeInvalid') : message;
}

export function DraftDefaultsSection({ form, disabled, t }: DraftDefaultsSectionProps) {
  const { field: defaultDateFromField } = useController({
    control: form.control,
    name: 'defaultDateFrom',
  });
  const { field: defaultDateToField } = useController({
    control: form.control,
    name: 'defaultDateTo',
  });

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <div className="space-y-1.5 md:col-span-2" data-testid="field-branding-mode">
        <Label htmlFor="create-offer-branding-mode">{t('create.fields.brandingMode')}</Label>
        <Controller
          control={form.control}
          name="brandingMode"
          render={({ field }) => (
            <SearchableSelectField
              data-testid="create-offer-branding-mode"
              disabled={disabled}
              options={BRANDING_MODE_OPTIONS.map((option) => ({
                label: t(option.labelKey),
                value: option.value,
              }))}
              placeholder={t('create.fields.brandingModePlaceholder')}
              value={typeof field.value === 'string' ? field.value : ''}
              onChange={(value) => field.onChange(value === '' ? undefined : value)}
            />
          )}
        />
      </div>

      <FormField
        label={t('create.fields.validUntil')}
        htmlFor="create-offer-valid-until"
        testId="field-valid-until"
        error={form.formState.errors.validUntil?.message}
      >
        <Controller
          control={form.control}
          name="validUntil"
          render={({ field }) => (
            <DatePickerButton
              id="create-offer-valid-until"
              aria-label={t('create.fields.validUntil')}
              testId="create-offer-valid-until"
              disabled={disabled}
              placeholder={t('create.fields.datePlaceholder')}
              locale={pl}
              value={formatDateValue(field.value)}
              onChange={(next) => field.onChange(parseOptionalDateInputValue(next))}
            />
          )}
        />
      </FormField>

      <CampaignDurationField
        id="create-offer-default-date-range"
        fieldTestId="field-default-date-range"
        testId="create-offer-default-date-range"
        label={t('create.fields.campaignDuration')}
        placeholder={t('create.fields.dateRangePlaceholder')}
        disabled={disabled}
        fromValue={defaultDateFromField.value}
        toValue={defaultDateToField.value}
        error={getDefaultDateToErrorMessage(form.formState.errors.defaultDateTo?.message, t)}
        onChange={({ from, to }) => {
          defaultDateFromField.onChange(from);
          defaultDateToField.onChange(to);
        }}
      />
    </div>
  );
}
